import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';
import type { OnboardingState, ReportSummary } from '@tallyframe/core';
import { Button, ButtonLink } from '../ui/Button';
import { Card } from '../ui/Card';
import { Icon } from '../ui/Icon';
import { IconButton } from '../ui/IconButton';
import { Stack } from '../ui/Stack';
import { AgentCard } from './AgentCard';
import { dismiss, takeFocusRequest, useDismissal } from './dismissal';
import { hasData, shareTarget } from './view';

export interface ChecklistProps {
  workspaceId: string;
  userId: string | null;
  reports: ReportSummary[];
  onboarding: OnboardingState;
  view: 'checklist' | 'set';
  agentCardOpen: boolean;
  /** Tiles render without their descriptions. */
  compact?: boolean;
  onTrySample(): void;
  onUpload(): void;
  /** Present only when `/api/config` advertises Google Sheets; without it there is no Sheet tile. */
  onSheet?: () => void;
}

const MUTED_TEXT = 'm-0 p-0 font-ui text-sm text-muted';
const PLAIN = 'm-0 p-0 border-0 bg-transparent';

function DoneStep({ label }: { label: string }): ReactElement {
  return (
    <li className={`${PLAIN} flex items-center gap-2 font-ui text-ink`}>
      <Icon name="check" />
      <span>{label}</span>
      <span className="sr-only">done</span>
    </li>
  );
}

function Tile({
  compact,
  description,
  children
}: {
  compact: boolean;
  description: string;
  children: ReactNode;
}): ReactElement {
  return (
    <Card>
      <Stack gap={2}>
        <div className={PLAIN}>{children}</div>
        {compact ? null : <p className={MUTED_TEXT}>{description}</p>}
      </Stack>
    </Card>
  );
}

export function Checklist({
  workspaceId,
  userId,
  reports,
  onboarding,
  view,
  agentCardOpen,
  compact = false,
  onTrySample,
  onUpload,
  onSheet
}: ChecklistProps): ReactElement | null {
  const dismissal = useDismissal(userId, workspaceId);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // On mount and on every reopen; takeFocusRequest is true once per reopen, so a remount stays quiet.
  useEffect(() => {
    if (userId && takeFocusRequest(userId, workspaceId)) headingRef.current?.focus();
  }, [userId, workspaceId, dismissal.focusRequest]);

  if (dismissal.checklist) return null;

  const step1 = hasData(reports);
  const step2 = onboarding.shared;
  const done = (step1 ? 1 : 0) + (step2 ? 1 : 0);
  const target = shareTarget(reports);

  return (
    <Stack gap={3}>
      <Card>
        <Stack gap={4}>
          <div className={`${PLAIN} flex items-start justify-between gap-3`}>
            <h2
              ref={headingRef}
              tabIndex={-1}
              className="m-0 p-0 font-ui text-lg font-semibold text-ink focus:outline-2 focus:outline-offset-2 focus:outline-ink"
            >
              {`Get started · ${done} of 2 done`}
            </h2>
            <IconButton
              label="Dismiss getting started"
              onPress={() => (userId ? dismiss(userId, workspaceId, 'checklist') : undefined)}
            >
              <span aria-hidden="true">×</span>
            </IconButton>
          </div>

          {view === 'set' ? <p className="m-0 p-0 font-ui font-semibold text-ink">You're set</p> : null}

          <ul className={`${PLAIN} list-none flex flex-col gap-4`}>
            {step1 ? (
              <DoneStep label="Make your first report" />
            ) : (
              <li className={PLAIN}>
                <Stack gap={3}>
                  <span className="font-ui font-semibold text-ink">Make your first report</span>
                  <div className={`${PLAIN} grid ${onSheet ? 'grid-cols-4' : 'grid-cols-3'} gap-4`}>
                    <Tile compact={compact} description="Explore a finished report with example data.">
                      <Button onPress={onTrySample}>Try a sample</Button>
                    </Tile>
                    <Tile compact={compact} description="Start from a spreadsheet export.">
                      <Button onPress={onUpload}>Upload CSV</Button>
                    </Tile>
                    {onSheet ? (
                      <Tile compact={compact} description="Pick a spreadsheet from your Google Drive.">
                        <Button onPress={onSheet}>From a Google Sheet</Button>
                      </Tile>
                    ) : null}
                    <Tile compact={compact} description="Connect your agent and ask it to build one.">
                      <ButtonLink href={`/w/${workspaceId}/agents`}>Ask your agent</ButtonLink>
                    </Tile>
                  </div>
                </Stack>
              </li>
            )}
            {step2 ? (
              <DoneStep label="Share it" />
            ) : (
              <li className={`${PLAIN} flex items-center gap-3 font-ui text-ink`}>
                <span className="sr-only">not done</span>
                {target ? (
                  <ButtonLink href={`/w/${workspaceId}/reports/${target.id}?share=1`}>Share it</ButtonLink>
                ) : (
                  <>
                    <span>Share it</span>
                    <Button isDisabled>Make a report first</Button>
                  </>
                )}
              </li>
            )}
          </ul>
        </Stack>
      </Card>
      {view === 'set' && agentCardOpen && !dismissal.agent ? (
        <AgentCard workspaceId={workspaceId} userId={userId} />
      ) : null}
    </Stack>
  );
}
